import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import OrderClient from '../../api/orderClient';
import { quickEta, getRejectPresets } from './orderActionHelpers';

// Every figure here comes straight from GET /metrics/dashboard — no sums,
// averages or date-bucketing happen client-side (CHANGES.md decision 4).
// This hook only fetches and lightly reshapes already-computed numbers into
// the props MetricCard/recharts expect (a delta sign picks a colour; that's
// presentation, not computation).
const useAdminDashboardPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const orderClient = useMemo(() => (config?.backendUrl ? new OrderClient(config.backendUrl) : null), [config]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Accept/reject straight from the "Recent orders" ⋯ menu — same
  // OrderClient endpoints and guards (ETA required to accept, a reason
  // required to reject) as Client Orders, just refreshing dashboard metrics
  // afterward instead of a paged order list.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const rejectPresets = getRejectPresets(t);

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptEta, setAcceptEta] = useState('');
  const [accepting, setAccepting] = useState(false);

  const fetchMetrics = useCallback((isRefresh = false) => {
    if (!config?.backendUrl || !companyId) return;
    if (isRefresh) setRefreshing(true); else setStatus('loading');
    axios.get(`${config.backendUrl}/metrics/dashboard`, { params: { companyId } })
      .then((res) => {
        setData(res.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching dashboard metrics', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /metrics/dashboard · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setRefreshing(false));
  }, [config, companyId, t]);

  useEffect(() => { fetchMetrics(false); }, [fetchMetrics]);

  const handleRefresh = () => fetchMetrics(true);

  // ---- Accept (requires ETA) -------------------------------------------//
  const openAcceptDialog = (order) => {
    setAcceptTarget(order);
    setAcceptEta('');
    setAcceptOpen(true);
  };
  const applyQuickEta = (kind) => setAcceptEta(quickEta(kind));

  const handleAccept = async () => {
    if (!acceptEta || !orderClient || !acceptTarget) return;
    setAccepting(true);
    try {
      await orderClient.acceptOrder(acceptTarget.orderId);
      try {
        await orderClient.setOrderEta(acceptTarget.orderId, acceptEta);
        notify(t('Order accepted and ETA updated successfully!'));
      } catch (etaError) {
        console.error('Error updating ETA:', etaError);
        notify(t('Order accepted but error updating ETA.'), 'warning');
      }
      setAcceptOpen(false);
      fetchMetrics(true);
    } catch (error) {
      console.error('Error accepting order:', error);
      notify(t('Error accepting order.'), 'error');
    } finally {
      setAccepting(false);
    }
  };

  // ---- Reject (requires a reason) --------------------------------------//
  const openRejectDialog = (order) => {
    setRejectTarget(order);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || !orderClient || !rejectTarget) return;
    setRejecting(true);
    try {
      await orderClient.rejectOrder(rejectTarget.orderId, rejectReason);
      notify(t('Order rejected successfully.'));
      setRejectOpen(false);
      fetchMetrics(true);
    } catch (error) {
      console.error('Error rejecting order:', error);
      notify(t('Error rejecting order.'), 'error');
    } finally {
      setRejecting(false);
    }
  };

  const isPreviewEmptyKind = previewState === 'empty';
  const effectiveStatus = previewState || status;
  const effectiveData = isPreviewEmptyKind ? {
    revenueToday: { value: 0, delta: null, sparkline: [] },
    ordersToday: { value: 0, delta: null, sparkline: [] },
    pendingOrders: { value: 0, oldestDays: null },
    outstandingTotal: { value: 0, delta: null, invoiceCount: 0 },
    revenueDaily: [],
    revenueLast14Days: { value: 0, delta: null },
    attention: [],
    lowStock: [],
    recentOrders: [],
  } : data;

  return {
    t, companyId, effectiveStatus, data: effectiveData, errorInfo,
    refreshing, handleRefresh, fetchMetrics,
    snackbar, setSnackbar,
    rejectOpen, setRejectOpen, rejectTarget, rejectReason, setRejectReason, rejecting, rejectPresets, openRejectDialog, handleReject,
    acceptOpen, setAcceptOpen, acceptTarget, acceptEta, setAcceptEta, accepting, applyQuickEta, openAcceptDialog, handleAccept,
  };
};

export default useAdminDashboardPage;
